import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { createPortal } from 'react-dom'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { AssignTaskModal } from '../components/common/AssignTaskModal'
import { TaskApprovalScreen } from '../components/features/TaskApprovalScreen'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { RichTextContent } from '../components/common/RichTextContent'
import { RichTextEditor } from '../components/common/RichTextEditor'
import { useAuth } from '../context/AuthContext'
import {
  RELATED_APPS,
  TASK_PRIORITIES,
  TASK_PRIORITY_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  TASK_TYPES,
  TASK_TYPE_LABELS,
} from '../lib/config/project-config'
import { isAssignableReviewRole, isSalesLikeRole } from '../lib/config/role-config'
import { canProfileSeeStatus, getVisibleStatusesForProfile } from '../lib/config/status-config'
import { mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { getTaskAttachmentValidationMessage, uploadTaskAttachment } from '../lib/supabase/storage'
import { Task, TaskAttachment } from '../lib/types/database.types'
import { exportTasksToExcel } from '../utils/exportToExcel'
import { sendTaskAssignedEmail, sendTaskNewApprovalEmail, sendTaskReviewOutcomeEmail } from '../utils/emailNotifications'
import { formatDate, formatDateTime, formatRelativeTime, stripHtml, truncateText } from '../utils/formatters'
import { getTaskCurrentStatusDueDate, getTaskStatusDueDate, isTaskOverallDueOverdue, isTaskStatusOverdue } from '../utils/statusDates'
import { validators } from '../utils/validators'
import './TaskModal.css'; // Assuming a CSS file for styling the metadata bubble

const TASKS_MY_TASKS_STORAGE_KEY = 'devtracker-tasks-my-tasks-only'
const TASKS_SORT_FIELD_KEY = 'devtracker-tasks-sort-field'
const TASKS_SORT_DIRECTION_KEY = 'devtracker-tasks-sort-direction'
const TASKS_GROUP_BY_STATUS_KEY = 'devtracker-tasks-group-by-status'
const TASKS_GROUP_BY_PRIORITY_KEY = 'devtracker-tasks-group-by-priority'
const TASKS_GROUP_BY_ASSIGNED_KEY = 'devtracker-tasks-group-by-assigned'
const TASKS_GROUP_BY_APP_KEY = 'devtracker-tasks-group-by-app'
const TASK_TABLE_SORT_FIELDS = ['title', 'status', 'priority', 'assigned_to', 'type', 'related_app', 'due'] as const

type TaskFormValues = {
  title: string
  description: string
  status: string
  type: string
  priority: string
  client_priority: string
  billable: boolean
  related_app_id: string
  support_ticket_no: string
  requested_by: string
  assigned_to: string
  projected_completion: string
  overall_due_date: string
  status_due_dates: Record<string, string>
  parent_task_id: string
  prerequisite_task_id: string
  estimated_hours: string
  release_version: string
  release_date: string
}

type SortField = typeof TASK_TABLE_SORT_FIELDS[number]
type SortDirection = 'asc' | 'desc'
type OverdueFilter = 'all' | 'any_overdue' | 'status_overdue' | 'task_overdue' | 'both_overdue' | 'not_overdue'
type UnassignedFilter = 'all' | 'yes' | 'no' | `user:${string}`

const defaultFormValues: TaskFormValues = {
  title: '',
  description: '',
  status: 'new',
  type: 'new_feature',
  priority: 'low',
  client_priority: 'low',
  billable: false,
  related_app_id: '',
  support_ticket_no: '',
  requested_by: '',
  assigned_to: '',
  projected_completion: '',
  overall_due_date: '',
  status_due_dates: {},
  parent_task_id: '',
  prerequisite_task_id: '',
  estimated_hours: '',
  release_version: '',
  release_date: '',
}

const getCreateDefaultFormValues = (_role?: string): TaskFormValues => ({
  ...defaultFormValues,
  status: 'new',
})

const createFormValues = (task?: Task | null): TaskFormValues => ({
  title: task?.title || '',
  description: task?.description || '',
  status: task?.status || 'new',
  type: task?.type || 'new_feature',
  priority: task?.priority || 'low',
  client_priority: task?.client_priority || 'low',
  billable: task?.billable || false,
  related_app_id: task?.related_app_id || '',
  support_ticket_no: task?.support_ticket_no || '',
  requested_by: task?.requested_by || '',
  assigned_to: task?.assigned_to || '',
  projected_completion: task ? getTaskCurrentStatusDueDate(task) || '' : '',
  overall_due_date: task?.overall_due_date || '',
  status_due_dates: task?.status_due_dates || {},
  parent_task_id: task?.parent_task_id || '',
  prerequisite_task_id: '',
  estimated_hours: task?.estimated_hours ? String(task.estimated_hours) : '',
  release_version: task?.release_version || '',
  release_date: task?.release_date || '',
})

const badgeClassName = 'inline-flex rounded-full border px-2.5 py-1 text-xs font-medium'
const modalSectionClassName = 'rounded-xl border border-stone-300 bg-stone-50 p-5'
const detailLabelClassName = 'text-xs uppercase tracking-wide text-gray-500'
const detailValueClassName = 'text-sm text-gray-800'
const taskSortCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })
const isTaskTableSortField = (value: string | null): value is SortField =>
  TASK_TABLE_SORT_FIELDS.includes(value as SortField)
const TASK_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg,.doc,.docx,.xls,.xlsx,.csv,.eml'

const normalizeResponsiblePerson = (value: string | null | undefined) => value?.trim().toLowerCase() || ''

const formatFileSize = (value: number | null) => {
  if (!value) {
    return 'Unknown size'
  }

  if (value < 1024) {
    return `${value} B`
  }

  const units = ['KB', 'MB', 'GB']
  let size = value / 1024
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }

  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`
}

const formatDateForInput = (value: Date) => value.toISOString().slice(0, 10)

const getAutoStatusDueDate = (status: string, statusDefaultDaysMap: Map<string, number>) => {
  const defaultDays = statusDefaultDaysMap.get(status)
  if (defaultDays === undefined) {
    return ''
  }

  const nextDate = new Date()
  nextDate.setDate(nextDate.getDate() + defaultDays)
  return formatDateForInput(nextDate)
}

export const TasksPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()
  const location = useLocation()
  const taskDetailsToggleRefs = useRef(new Map<string, HTMLButtonElement>())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [assigningTask, setAssigningTask] = useState<Task | null>(null)
  const [selectedAssignee, setSelectedAssignee] = useState('')
  const [assignError, setAssignError] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<TaskFormValues>(getCreateDefaultFormValues())
  const [formError, setFormError] = useState<string | null>(null)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [noteError, setNoteError] = useState<string | null>(null)
  const [selectedAttachmentFile, setSelectedAttachmentFile] = useState<File | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isViewOnly, setIsViewOnly] = useState(false)
  const [subTaskReturnContext, setSubTaskReturnContext] = useState<{
    parentTaskId: string
    isViewOnly: boolean
  } | null>(null)
  const [myTasksOnly, setMyTasksOnly] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true
    }

    const storedValue = window.localStorage.getItem(TASKS_MY_TASKS_STORAGE_KEY)
    return storedValue ? storedValue === 'true' : true
  })
    const [includeCompleted, setIncludeCompleted] = useState(false)
    const [overdueFilter, setOverdueFilter] = useState<OverdueFilter>('all')
    const [unassignedFilter, setUnassignedFilter] = useState<UnassignedFilter>('all')
    const [sortField, setSortField] = useState<SortField>(() => {
      if (typeof window === 'undefined') return 'title'
      const stored = window.localStorage.getItem(TASKS_SORT_FIELD_KEY)
      return isTaskTableSortField(stored) ? stored : 'title'
    })
    const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
      if (typeof window === 'undefined') return 'desc'
      const stored = window.localStorage.getItem(TASKS_SORT_DIRECTION_KEY)
      return stored as SortDirection || 'desc'
    })
    const [searchQuery, setSearchQuery] = useState('')
    const [groupByStatus, setGroupByStatus] = useState<boolean>(() => {
      if (typeof window === 'undefined') return false
      const stored = window.localStorage.getItem(TASKS_GROUP_BY_STATUS_KEY)
      return stored ? stored === 'true' : false
    })
    const [groupByPriority, setGroupByPriority] = useState<boolean>(() => {
      if (typeof window === 'undefined') return false
      const stored = window.localStorage.getItem(TASKS_GROUP_BY_PRIORITY_KEY)
      return stored ? stored === 'true' : false
    })
    const [groupByAssignedTo, setGroupByAssignedTo] = useState<boolean>(() => {
      if (typeof window === 'undefined') return false
      const stored = window.localStorage.getItem(TASKS_GROUP_BY_ASSIGNED_KEY)
      return stored ? stored === 'true' : false
    })
    const [groupByRelatedApp, setGroupByRelatedApp] = useState<boolean>(() => {
      if (typeof window === 'undefined') return false
      const stored = window.localStorage.getItem(TASKS_GROUP_BY_APP_KEY)
      return stored ? stored === 'true' : false
    })
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null)
    const [taskDetailsPosition, setTaskDetailsPosition] = useState<{ top: number; left: number; width: number } | null>(null)
    const [inlineEditTaskId, setInlineEditTaskId] = useState<string | null>(null)
    const [inlineEditField, setInlineEditField] = useState<'status' | 'priority' | null>(null)
    const [inlineEditValue, setInlineEditValue] = useState<string>('')
    const [shouldAutoOpenReview, setShouldAutoOpenReview] = useState(false)
    const [reviewActionTaskId, setReviewActionTaskId] = useState<string | null>(null)

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => queries.getTasks(false),
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const statusDefaultsQuery = useQuery({
    queryKey: ['task-status-default-days'],
    queryFn: queries.getTaskStatusDefaultDays,
  })

  const taskNotesQuery = useQuery({
    queryKey: ['task-notes', editingTask?.id],
    queryFn: () => queries.getTaskNotes(editingTask!.id),
    enabled: Boolean(editingTask?.id && isModalOpen),
  })

  const taskAttachmentsQuery = useQuery({
    queryKey: ['task-attachments', editingTask?.id],
    queryFn: () => queries.getTaskAttachments(editingTask!.id),
    enabled: Boolean(editingTask?.id && isModalOpen),
  })

  const taskPrerequisitesQuery = useQuery({
    queryKey: ['task-prerequisites'],
    queryFn: queries.getTaskPrerequisites,
  })

  const onMutationSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
      queryClient.invalidateQueries({ queryKey: ['task-prerequisites'] }),
    ])

    if (subTaskReturnContext) {
      const refreshedTasks = await queryClient.fetchQuery({
        queryKey: ['tasks'],
        queryFn: () => queries.getTasks(false),
      })
      const parentTask = refreshedTasks.find((task) => task.id === subTaskReturnContext.parentTaskId) || null

      setIsModalOpen(Boolean(parentTask))
      setEditingTask(parentTask)
      setIsViewOnly(subTaskReturnContext.isViewOnly)
      setFormValues(parentTask ? createFormValues(parentTask) : getCreateDefaultFormValues(profile?.role))
      setFormError(null)
      setNewNoteContent('')
      setNoteError(null)
      setSelectedAttachmentFile(null)
      setAttachmentError(null)
      setSubTaskReturnContext(null)
      return
    }

    setIsModalOpen(false)
    setEditingTask(null)
    setIsViewOnly(false)
    setFormValues(getCreateDefaultFormValues(profile?.role))
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setSelectedAttachmentFile(null)
    setAttachmentError(null)
  }

  const createTaskMutation = useMutation({
    mutationFn: mutations.createTask,
    onSuccess: onMutationSuccess,
  })

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: Parameters<typeof mutations.updateTask>[1] }) =>
      mutations.updateTask(taskId, updates),
    onSuccess: onMutationSuccess,
  })

  const deleteTaskMutation = useMutation({
    mutationFn: mutations.deleteTask,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
      ])
    },
  })

  const createTaskNoteMutation = useMutation({
    mutationFn: mutations.createTaskNote,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task-notes', editingTask?.id] })
      setNewNoteContent('')
      setNoteError(null)
    },
  })

  const createTaskAttachmentMutation = useMutation({
    mutationFn: async ({ taskId, file, uploadedBy }: { taskId: string; file: File; uploadedBy: string }) => {
      const uploadedFile = await uploadTaskAttachment(taskId, file)

      return mutations.createTaskAttachment({
        task_id: taskId,
        file_name: uploadedFile.fileName,
        storage_path: uploadedFile.storagePath,
        mime_type: uploadedFile.mimeType,
        file_size_bytes: uploadedFile.fileSizeBytes,
        uploaded_by: uploadedBy,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task-attachments', editingTask?.id] })
      setSelectedAttachmentFile(null)
      setAttachmentError(null)
    },
  })

  const setTaskPrerequisiteMutation = useMutation({
    mutationFn: ({ taskId, prerequisiteTaskId }: { taskId: string; prerequisiteTaskId: string | null }) =>
      mutations.setTaskPrerequisite(taskId, prerequisiteTaskId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task-prerequisites'] })
    },
  })

  const profileOptions = profilesQuery.data || []
  const relatedAppOptions = relatedAppsQuery.data || RELATED_APPS.map((name) => ({
    id: name,
    name,
    current_version: null,
    next_release_version: null,
    responsible_person: null,
    created_at: '',
    updated_at: '',
  }))
  const assigneeLabelMap = useMemo(
    () => new Map(profileOptions.map((item) => [item.id, item.full_name || item.email || 'Unknown user'])),
    [profileOptions]
  )
  const assignedToFilterOptions = useMemo(() => {
    const visibleTasks = (tasksQuery.data || []).filter((task) => canProfileSeeStatus(profile, task.status))
    const uniqueAssignedUserIds = Array.from(
      new Set(
        visibleTasks
          .map((task) => task.assigned_to)
          .filter((assignedTo): assignedTo is string => Boolean(assignedTo))
      )
    )

    return uniqueAssignedUserIds
      .map((id) => ({ id, label: assigneeLabelMap.get(id) || id }))
      .sort((left, right) => taskSortCollator.compare(left.label, right.label))
  }, [tasksQuery.data, profile, assigneeLabelMap])
  const relatedAppResponsiblePersonMap = useMemo(() => {
    const normalizedProfiles = profileOptions.map((item) => ({
      id: item.id,
      fullName: normalizeResponsiblePerson(item.full_name),
      email: normalizeResponsiblePerson(item.email),
    }))

    return new Map(
      relatedAppOptions.map((app) => {
        const responsiblePerson = normalizeResponsiblePerson(app.responsible_person)

        if (!responsiblePerson) {
          return [app.id, null]
        }

        const matchedProfile = normalizedProfiles.find(
          (item) => item.id === responsiblePerson || item.fullName === responsiblePerson || item.email === responsiblePerson
        )

        return [app.id, matchedProfile?.id || null]
      })
    )
  }, [profileOptions, relatedAppOptions])
  const reviewAssigneeOptions = useMemo(
    () =>
      profileOptions
        .filter((item) => isAssignableReviewRole(item.role))
        .map((item) => ({ id: item.id, label: item.full_name || item.email || 'Unknown user' })),
    [profileOptions]
  )
  const selectableStatuses = useMemo(
    () => getVisibleStatusesForProfile(profile),
    [profile]
  )
  const statusDefaultDaysMap = useMemo(
    () => new Map((statusDefaultsQuery.data || []).map((item) => [item.status, item.default_days])),
    [statusDefaultsQuery.data]
  )

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.setItem(TASKS_MY_TASKS_STORAGE_KEY, String(myTasksOnly))
  }, [myTasksOnly])

  React.useEffect(() => {
    if (!unassignedFilter.startsWith('user:')) {
      return
    }

    const selectedAssigneeId = unassignedFilter.slice('user:'.length)
    const existsInOptions = assignedToFilterOptions.some((option) => option.id === selectedAssigneeId)
    if (!existsInOptions) {
      setUnassignedFilter('all')
    }
  }, [unassignedFilter, assignedToFilterOptions])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_SORT_FIELD_KEY, sortField)
    }, [sortField])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_SORT_DIRECTION_KEY, sortDirection)
    }, [sortDirection])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_GROUP_BY_STATUS_KEY, String(groupByStatus))
    }, [groupByStatus])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_GROUP_BY_PRIORITY_KEY, String(groupByPriority))
    }, [groupByPriority])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_GROUP_BY_ASSIGNED_KEY, String(groupByAssignedTo))
    }, [groupByAssignedTo])

    React.useEffect(() => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(TASKS_GROUP_BY_APP_KEY, String(groupByRelatedApp))
    }, [groupByRelatedApp])

    // Handle newly created task from location state (e.g., from new dev request)
    useEffect(() => {
      const state = (location as unknown as { state?: { newlyCreatedTask?: Task } }).state
      const newlyCreatedTask = state?.newlyCreatedTask
      const dashboardTaskFilter = (state as { dashboardTaskFilter?: 'unassigned' | 'overdue' } | undefined)?.dashboardTaskFilter

      if (dashboardTaskFilter === 'unassigned') {
        setMyTasksOnly(false)
        setUnassignedFilter('yes')
        setOverdueFilter('all')
        window.history.replaceState({}, document.title, window.location.pathname)
        return
      }

      if (dashboardTaskFilter === 'overdue') {
        setMyTasksOnly(false)
        setOverdueFilter('any_overdue')
        setUnassignedFilter('all')
        window.history.replaceState({}, document.title, window.location.pathname)
        return
      }

      if (newlyCreatedTask && isSalesLikeRole(profile?.role)) {
        // Open view modal for the newly created task
        setEditingTask(newlyCreatedTask)
        setIsModalOpen(true)
        setIsViewOnly(true)
        setFormValues(createFormValues(newlyCreatedTask))
        
        // Clear the location state so it doesn't persist on navigation
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    }, [location, profile?.role])

    useEffect(() => {
      if (!profile?.is_task_approver) {
        return
      }

      const params = new URLSearchParams(location.search)
      if (params.get('review') !== 'pending') {
        return
      }
      setShouldAutoOpenReview(true)

      params.delete('review')
      const nextQuery = params.toString()
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`
      window.history.replaceState({}, document.title, nextUrl)
    }, [location.search, profile?.is_task_approver])

    useEffect(() => {
      const handlePointerDown = (event: MouseEvent) => {
        const target = event.target
        if (!(target instanceof HTMLElement) || !expandedTaskId) {
          return
        }

        const taskDetailsPanel = target.closest('[data-task-details-panel]')
        if (taskDetailsPanel?.getAttribute('data-task-details-panel') === expandedTaskId) {
          return
        }

        const taskDetailsToggle = target.closest('[data-task-details-toggle]')
        if (taskDetailsToggle?.getAttribute('data-task-details-toggle') === expandedTaskId) {
          return
        }

        setExpandedTaskId(null)
        setTaskDetailsPosition(null)
      }

      document.addEventListener('mousedown', handlePointerDown)
      return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [expandedTaskId])

    useEffect(() => {
      if (!expandedTaskId) {
        return
      }

      const updatePosition = () => {
        const anchor = taskDetailsToggleRefs.current.get(expandedTaskId)
        if (!anchor) {
          setExpandedTaskId(null)
          setTaskDetailsPosition(null)
          return
        }

        const rect = anchor.getBoundingClientRect()
        const width = Math.min(512, window.innerWidth - 32)
        const estimatedHeight = 360
        const left = Math.min(Math.max(16, rect.right - width), window.innerWidth - width - 16)
        const belowTop = rect.bottom + 8
        const aboveTop = rect.top - estimatedHeight - 8
        const top = belowTop + estimatedHeight <= window.innerHeight - 16 ? belowTop : Math.max(16, aboveTop)

        setTaskDetailsPosition({ top, left, width })
      }

      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)

      return () => {
        window.removeEventListener('resize', updatePosition)
        window.removeEventListener('scroll', updatePosition, true)
      }
    }, [expandedTaskId])
  const isSaving = createTaskMutation.isPending || updateTaskMutation.isPending || setTaskPrerequisiteMutation.isPending
  const pageError =
    tasksQuery.error ||
    profilesQuery.error ||
    statusDefaultsQuery.error ||
    deleteTaskMutation.error ||
    createTaskNoteMutation.error ||
    taskPrerequisitesQuery.error ||
    taskAttachmentsQuery.error ||
    createTaskAttachmentMutation.error

  const newRequestTaskList = useMemo(
    () => (tasksQuery.data || []).filter((task) => task.status === 'new'),
    [tasksQuery.data]
  )

  const openCreateModal = () => {
    const initialStatus = profile?.role === 'admin' ? 'new' : 'new'
    const autoDueDate = getAutoStatusDueDate(initialStatus, statusDefaultDaysMap)
    setIsViewOnly(false)
    setSubTaskReturnContext(null)
    setEditingTask(null)
    setFormValues({
      ...getCreateDefaultFormValues(profile?.role),
      related_app_id: relatedAppOptions[0]?.id || '',
      projected_completion: autoDueDate,
      status_due_dates: autoDueDate ? { [initialStatus]: autoDueDate } : {},
      prerequisite_task_id: '',
    })
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setSelectedAttachmentFile(null)
    setAttachmentError(null)
    setIsModalOpen(true)
  }

  const openCreateSubTaskModal = (parentTask: Task, returnToParentView = false) => {
    if (parentTask.type !== 'new_feature') {
      return
    }

    setSubTaskReturnContext(
      returnToParentView
        ? {
            parentTaskId: parentTask.id,
            isViewOnly,
          }
        : null
    )
    setIsViewOnly(false)
    setEditingTask(null)
    setFormValues({
      ...getCreateDefaultFormValues(profile?.role),
      status: 'approved',
      type: parentTask.type,
      priority: parentTask.priority,
      client_priority: parentTask.client_priority,
      billable: parentTask.billable,
      related_app_id: parentTask.related_app_id || relatedAppOptions[0]?.id || '',
      support_ticket_no: '',
      requested_by: parentTask.requested_by,
      assigned_to: parentTask.assigned_to || '',
      projected_completion: getTaskStatusDueDate(parentTask, 'approved') || '',
      status_due_dates: {
        approved: getTaskStatusDueDate(parentTask, 'approved') || '',
      },
      release_version: parentTask.release_version || '',
      release_date: parentTask.release_date || '',
      parent_task_id: parentTask.id,
      prerequisite_task_id: '',
    })
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setSelectedAttachmentFile(null)
    setAttachmentError(null)
    setIsModalOpen(true)
  }

  const openEditModal = (task: Task) => {
    if (isSalesLikeRole(profile?.role)) {
      return // Sales users cannot edit
    }
    setIsViewOnly(false)
    setEditingTask(task)
    setFormValues({
      ...createFormValues(task),
      prerequisite_task_id: prerequisiteByTaskId.get(task.id) || '',
    })
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setSelectedAttachmentFile(null)
    setAttachmentError(null)
    setIsModalOpen(true)
  }

  const openViewModal = (task: Task) => {
    setIsViewOnly(true)
    setEditingTask(task)
    setFormValues({
      ...createFormValues(task),
      prerequisite_task_id: prerequisiteByTaskId.get(task.id) || '',
    })
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setIsModalOpen(true)
  }

  const openAssignModal = (task: Task) => {
    if (isSalesLikeRole(profile?.role)) {
      return
    }

    setAssigningTask(task)
    setSelectedAssignee(task.assigned_to || '')
    setAssignError(null)
  }

  const closeAssignModal = () => {
    if (updateTaskMutation.isPending) {
      return
    }

    setAssigningTask(null)
    setSelectedAssignee('')
    setAssignError(null)
  }

  const openInlineEdit = (taskId: string, field: 'status' | 'priority', currentValue: string) => {
    setInlineEditTaskId(taskId)
    setInlineEditField(field)
    setInlineEditValue(currentValue)
  }

  const closeInlineEdit = () => {
    setInlineEditTaskId(null)
    setInlineEditField(null)
    setInlineEditValue('')
  }

  const handleInlineEditSave = async () => {
    if (!inlineEditTaskId || !inlineEditField) return

    try {
      await updateTaskMutation.mutateAsync({
        taskId: inlineEditTaskId,
        updates: {
          [inlineEditField]: inlineEditValue,
        },
      })
      closeInlineEdit()
    } catch (error) {
      console.error('Failed to update task:', error)
    }
  }

  const handleReviewStatusChange = async (task: Task, status: 'approved' | 'rejected', assigneeId: string | null = null) => {
    setReviewActionTaskId(task.id)

    try {
      const updates: Parameters<typeof mutations.updateTask>[1] = { status }
      if (status === 'approved') {
        updates.assigned_to = assigneeId
      }

      await updateTaskMutation.mutateAsync({
        taskId: task.id,
        updates,
      })

      const updatedTask: Task = {
        ...task,
        status,
        assigned_to: status === 'approved' ? assigneeId : task.assigned_to,
      }

      const notifications: Array<Promise<boolean>> = []
      const creatorProfile = profileOptions.find((item) => item.id === task.created_by)
      const creatorEmail = creatorProfile?.email
      const reviewerName = profile?.full_name || profile?.email || 'Task approver'
      if (creatorEmail) {
        notifications.push(sendTaskReviewOutcomeEmail(creatorEmail, updatedTask, status, reviewerName))
      }

      if (status === 'approved' && assigneeId) {
        const assignedProfile = profileOptions.find((item) => item.id === assigneeId)
        if (assignedProfile?.email) {
          notifications.push(sendTaskAssignedEmail(assignedProfile.email, updatedTask))
        }
      }

      if (notifications.length > 0) {
        await Promise.allSettled(notifications)
      }
    } finally {
      setReviewActionTaskId(null)
    }
  }

  const closeModal = () => {
    if (isSaving) {
      return
    }

    setIsModalOpen(false)
    setIsViewOnly(false)
    setEditingTask(null)
    setFormValues(defaultFormValues)
    setFormError(null)
    setNewNoteContent('')
    setNoteError(null)
    setSelectedAttachmentFile(null)
    setAttachmentError(null)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const titleError = validators.getTaskTitleError(formValues.title)
    if (titleError) {
      setFormError(titleError)
      return
    }

    const descriptionError = validators.getTaskDescriptionError(formValues.description)
    if (descriptionError) {
      setFormError(descriptionError)
      return
    }
      // Only admin can change status to/from 'new'
      if (profile?.role !== 'admin' && editingTask) {
        const isChangingFromNew = editingTask.status === 'new' && formValues.status !== 'new'
        const isChangingToNew = editingTask.status !== 'new' && formValues.status === 'new'
        if (isChangingFromNew || isChangingToNew) {
          setFormError('Only administrators can move tasks to/from "New" status.')
          return
        }
      }

    if (!user) {
      setFormError('You must be signed in to manage tasks.')
      return
    }

    setFormError(null)

    const selectedPrerequisiteTask = allTasks.find((task) => task.id === formValues.prerequisite_task_id)
    const prerequisitesMet = !formValues.prerequisite_task_id || selectedPrerequisiteTask?.status === 'released_to_dev'
    const statusDueDates = { ...formValues.status_due_dates }
    if (formValues.projected_completion) {
      statusDueDates[formValues.status] = formValues.projected_completion
    } else {
      delete statusDueDates[formValues.status]
    }
    const canEditSupportTicket = profile?.role === 'admin' || profile?.role === 'support'
    const canEditOverallDueDate = profile?.role === 'admin'

    const payload = {
      title: formValues.title.trim(),
      description: formValues.description.trim() || null,
      status: editingTask ? formValues.status : profile?.role === 'admin' ? formValues.status : 'new',
      type: formValues.type,
      priority: formValues.priority,
      client_priority: formValues.client_priority,
      billable: formValues.billable,
      related_app_id: formValues.related_app_id || null,
      support_ticket_no: formValues.parent_task_id || !canEditSupportTicket
        ? null
        : (formValues.support_ticket_no.trim() || null),
      requested_by: formValues.requested_by.trim(),
      assigned_to: formValues.assigned_to || null,
      projected_completion: statusDueDates[formValues.status] || null,
      overall_due_date: canEditOverallDueDate
        ? (formValues.overall_due_date || null)
        : (editingTask?.overall_due_date || null),
      status_due_dates: statusDueDates,
      parent_task_id: formValues.parent_task_id || null,
      prerequisets_met: prerequisitesMet,
      estimated_hours: formValues.estimated_hours ? parseFloat(formValues.estimated_hours) : null,
      release_version: formValues.release_version.trim() || null,
      release_date: formValues.release_date || null,
    }

    try {
      if (editingTask) {
        const updatedTask = await updateTaskMutation.mutateAsync({
          taskId: editingTask.id,
          updates: payload,
        })
        await setTaskPrerequisiteMutation.mutateAsync({
          taskId: updatedTask.id,
          prerequisiteTaskId: payload.parent_task_id ? (formValues.prerequisite_task_id || null) : null,
        })
      } else {
        const createdTask = await createTaskMutation.mutateAsync({
          ...payload,
          created_by: user.id,
        })
        await setTaskPrerequisiteMutation.mutateAsync({
          taskId: createdTask.id,
          prerequisiteTaskId: payload.parent_task_id ? (formValues.prerequisite_task_id || null) : null,
        })

        if (createdTask.status === 'new') {
          const approverEmails = profileOptions
            .filter((item) => item.is_task_approver && item.email)
            .map((item) => item.email as string)

          if (approverEmails.length > 0) {
            await sendTaskNewApprovalEmail(approverEmails, createdTask, payload.requested_by || 'Unknown requester')
          }
        }
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to save task.')
    }
  }

  const handleDelete = async (task: Task) => {
    const confirmed = window.confirm(`Delete "${task.title}"?`)
    if (!confirmed) {
      return
    }

    await deleteTaskMutation.mutateAsync(task.id)
  }

  const handleAssignTask = async () => {
    if (!assigningTask || !selectedAssignee) {
      setAssignError('Please select a user to assign this task.')
      return
    }

    setAssignError(null)

    try {
      await updateTaskMutation.mutateAsync({
        taskId: assigningTask.id,
        updates: { assigned_to: selectedAssignee },
      })

      // Send email notification to the assigned user
      const assignedProfile = profileOptions.find((p) => p.id === selectedAssignee)
      if (assignedProfile?.email) {
        await sendTaskAssignedEmail(assignedProfile.email, assigningTask)
      }

      setAssigningTask(null)
      setSelectedAssignee('')
    } catch (error) {
      setAssignError(error instanceof Error ? error.message : 'Unable to assign task.')
    }
  }

  const handleAddNote = async () => {
    if (!editingTask || !user) {
      return
    }

    const plainText = stripHtml(newNoteContent)
    if (!plainText) {
      setNoteError('Note content is required.')
      return
    }

    setNoteError(null)

    try {
      await createTaskNoteMutation.mutateAsync({
        task_id: editingTask.id,
        content: newNoteContent,
        created_by: user.id,
      })
    } catch (error) {
      setNoteError(error instanceof Error ? error.message : 'Unable to add note.')
    }
  }

  const handleAttachmentFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    if (!file) {
      setSelectedAttachmentFile(null)
      setAttachmentError(null)
      return
    }

    const validationMessage = getTaskAttachmentValidationMessage(file)
    if (validationMessage) {
      setSelectedAttachmentFile(null)
      setAttachmentError(validationMessage)
      event.target.value = ''
      return
    }

    setSelectedAttachmentFile(file)
    setAttachmentError(null)
  }

  const handleUploadAttachment = async () => {
    if (!editingTask) {
      setAttachmentError('Create the task first before uploading attachments.')
      return
    }

    if (!user) {
      setAttachmentError('You must be signed in to upload attachments.')
      return
    }

    if (!selectedAttachmentFile) {
      setAttachmentError('Choose a file to upload.')
      return
    }

    setAttachmentError(null)

    try {
      await createTaskAttachmentMutation.mutateAsync({
        taskId: editingTask.id,
        file: selectedAttachmentFile,
        uploadedBy: user.id,
      })
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : 'Unable to upload attachment.')
    }
  }

  const handleOpenAttachment = async (attachment: TaskAttachment) => {
    try {
      const accessUrl = await queries.getTaskAttachmentAccessUrl(attachment)
      window.open(accessUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : 'Unable to open attachment.')
    }
  }

  const tasks = useMemo(() => {
    let nextTasks = tasksQuery.data || []

    nextTasks = nextTasks.filter((task) => canProfileSeeStatus(profile, task.status))

    if (!includeCompleted) {
      nextTasks = nextTasks.filter((task) => task.status !== 'completed')
    }

    const isOverdueByFilter = (task: Task) => {
      const statusOverdue = isTaskStatusOverdue(task)
      const taskOverdue = isTaskOverallDueOverdue(task)

      switch (overdueFilter) {
        case 'any_overdue':
          return statusOverdue || taskOverdue
        case 'status_overdue':
          return statusOverdue
        case 'task_overdue':
          return taskOverdue
        case 'both_overdue':
          return statusOverdue && taskOverdue
        case 'not_overdue':
          return !statusOverdue && !taskOverdue
        default:
          return true
      }
    }

    // My Tasks filtering - role-dependent and subtask-aware
    if (myTasksOnly && user) {
      if (isSalesLikeRole(profile?.role)) {
        const directlyMatchedTasks = nextTasks.filter((task) => task.created_by === user.id || task.assigned_to === user.id)
        const visibleParentIds = new Set(
          directlyMatchedTasks.filter((task) => !task.parent_task_id).map((task) => task.id)
        )

        nextTasks = nextTasks.filter(
          (task) =>
            task.created_by === user.id ||
            task.assigned_to === user.id ||
            (Boolean(task.parent_task_id) && visibleParentIds.has(task.parent_task_id!))
        )
      } else {
        // Dev & Admin users: My Tasks = tasks assigned to them
        nextTasks = nextTasks.filter((task) => task.assigned_to === user.id)
      }

      // My Tasks should never include unassigned records.
      nextTasks = nextTasks.filter((task) => Boolean(task.assigned_to))
    }

    nextTasks = nextTasks.filter(isOverdueByFilter)

    if (unassignedFilter === 'yes') {
      nextTasks = nextTasks.filter((task) => !task.assigned_to)
    } else if (unassignedFilter === 'no') {
      nextTasks = nextTasks.filter((task) => Boolean(task.assigned_to))
    } else if (unassignedFilter.startsWith('user:')) {
      const assigneeId = unassignedFilter.slice('user:'.length)
      nextTasks = nextTasks.filter((task) => task.assigned_to === assigneeId)
    }

    // Search filtering - searches all fields
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      nextTasks = nextTasks.filter((task) => {
        const searchFields = [
          task.title,
          task.status,
          task.priority,
          task.type,
          task.related_app,
          task.requested_by,
          task.created_by ? assigneeLabelMap.get(task.created_by) || task.created_by : '',
          task.assigned_to ? profilesQuery.data?.find((p) => p.id === task.assigned_to)?.full_name || task.assigned_to : '',
          stripHtml(task.description || ''),
        ]
        return searchFields.some(field => (field || '').toString().toLowerCase().includes(query))
      })
    }

    const statusOrder = Object.fromEntries(selectableStatuses.map((status, index) => [status, index])) as Record<string, number>
    const priorityOrder = Object.fromEntries(TASK_PRIORITIES.map((priority, index) => [priority, index])) as Record<string, number>
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1
    const compareText = (left?: string | null, right?: string | null) =>
      taskSortCollator.compare(left || '', right || '')
    const compareDate = (left?: string | null, right?: string | null) => {
      const leftValue = left ? new Date(left).getTime() : Number.NEGATIVE_INFINITY
      const rightValue = right ? new Date(right).getTime() : Number.NEGATIVE_INFINITY
      return leftValue - rightValue
    }

    const compareTasks = (left: Task, right: Task) => {
      switch (sortField) {
        case 'title':
          return compareText(left.title, right.title)
        case 'status':
          return (statusOrder[left.status] ?? Number.MAX_SAFE_INTEGER) - (statusOrder[right.status] ?? Number.MAX_SAFE_INTEGER)
        case 'priority':
          return (priorityOrder[left.priority] ?? Number.MAX_SAFE_INTEGER) - (priorityOrder[right.priority] ?? Number.MAX_SAFE_INTEGER)
        case 'type':
          return compareText(TASK_TYPE_LABELS[left.type as keyof typeof TASK_TYPE_LABELS] || left.type, TASK_TYPE_LABELS[right.type as keyof typeof TASK_TYPE_LABELS] || right.type)
        case 'related_app':
          return compareText(left.related_app, right.related_app)
        case 'assigned_to':
          return compareText(assigneeLabelMap.get(left.assigned_to || '') || left.assigned_to || 'Unassigned', assigneeLabelMap.get(right.assigned_to || '') || right.assigned_to || 'Unassigned')
        case 'due':
          return compareDate(getTaskCurrentStatusDueDate(left), getTaskCurrentStatusDueDate(right))
        default:
          return compareText(left.title, right.title)
      }
    }

    nextTasks = [...nextTasks].sort((left, right) => {
      const result = compareTasks(left, right)
      if (result !== 0) {
        return result * directionMultiplier
      }

      return taskSortCollator.compare(left.id, right.id)
    })

    const childTasksByParent = new Map<string, Task[]>()
    const rootTasks: Task[] = []
    const visibleTaskIds = new Set(nextTasks.map((task) => task.id))

    nextTasks.forEach((task) => {
      if (task.parent_task_id && visibleTaskIds.has(task.parent_task_id)) {
        const siblings = childTasksByParent.get(task.parent_task_id) || []
        siblings.push(task)
        childTasksByParent.set(task.parent_task_id, siblings)
      } else {
        rootTasks.push(task)
      }
    })

    const orderedTasks: Task[] = []
    const appendTaskTree = (task: Task) => {
      orderedTasks.push(task)
      const children = childTasksByParent.get(task.id) || []
      children.forEach(appendTaskTree)
    }

    rootTasks.forEach(appendTaskTree)

    return orderedTasks
  }, [includeCompleted, myTasksOnly, overdueFilter, unassignedFilter, tasksQuery.data, user, profile, searchQuery, profilesQuery.data, assigneeLabelMap, selectableStatuses, sortDirection, sortField])
  const notes = taskNotesQuery.data || []
  const attachments = taskAttachmentsQuery.data || []
  const totalTasks = tasks.length
  const openTasks = tasks.filter((task) => task.status !== 'completed').length
  const allTasks = tasksQuery.data || []
  const parentTask = editingTask?.parent_task_id
    ? allTasks.find((task) => task.id === editingTask.parent_task_id) || null
    : null
  const createdByName = editingTask?.created_by ? assigneeLabelMap.get(editingTask.created_by) || 'Unknown user' : 'Unknown user'
  const assignedToName = editingTask?.assigned_to ? assigneeLabelMap.get(editingTask.assigned_to) || 'Unknown user' : 'Unassigned'
  const attachmentEmptyState = editingTask
    ? 'No attachments yet.'
    : 'Save the task first, then upload attachments into its task folder.'

  const renderAttachmentsSection = () => (
    <section className={clsx(modalSectionClassName, 'xl:col-span-2')}>
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Attachments</h3>
          <p className="text-sm text-gray-500">Images, Office documents, CSV, and EML files</p>
        </div>
      </div>

      {!editingTask ? (
        <p className="text-sm text-gray-500">{attachmentEmptyState}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <label className="space-y-2">
              <span className="text-sm font-medium text-gray-700">Upload Attachment</span>
              <input
                type="file"
                accept={TASK_ATTACHMENT_ACCEPT}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-stone-200 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-900"
                onChange={handleAttachmentFileChange}
              />
            </label>
            <Button
              type="button"
              onClick={handleUploadAttachment}
              isLoading={createTaskAttachmentMutation.isPending}
              disabled={!selectedAttachmentFile}
            >
              Upload Attachment
            </Button>
          </div>

          {selectedAttachmentFile && (
            <p className="text-xs text-gray-500">
              Ready to upload: {selectedAttachmentFile.name} ({formatFileSize(selectedAttachmentFile.size)})
            </p>
          )}

          {attachmentError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {attachmentError}
            </div>
          )}

          {taskAttachmentsQuery.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <LoadingSpinner text="Loading attachments..." />
            </div>
          ) : attachments.length === 0 ? (
            <p className="text-sm text-gray-500">{attachmentEmptyState}</p>
          ) : (
            <div className="space-y-3">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white/80 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{attachment.file_name}</p>
                    <p className="text-xs text-gray-500">
                      Added by {attachment.uploader_name} on {formatDateTime(attachment.created_at)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {attachment.mime_type || 'Unknown type'} | {formatFileSize(attachment.file_size_bytes)}
                    </p>
                  </div>
                  <div>
                    <Button type="button" variant="secondary" size="sm" onClick={() => handleOpenAttachment(attachment)}>
                      Open
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )

  const renderSubTasksSection = (task: Task) => {
    if (task.parent_task_id) {
      return null
    }

    const subTasks = subTasksByParentId.get(task.id) || []

    return (
      <section className={clsx(modalSectionClassName, 'xl:col-span-2')}>
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900">Sub-tasks</h3>
          <p className="text-sm text-gray-500">Ordered child tasks and readiness status</p>
        </div>

        {subTasks.length === 0 ? (
          <p className="text-sm text-gray-500">No sub-tasks yet.</p>
        ) : (
          <div className="space-y-2">
            {subTasks.map((subTask) => (
              <div key={subTask.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white/80 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {(subTask.sub_task_order || 0) > 0 ? `${subTask.sub_task_order}. ` : ''}
                    {subTask.title}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    {renderReadinessBadge(subTask)}
                    <span className="text-xs text-gray-500">{TASK_STATUS_LABELS[subTask.status as keyof typeof TASK_STATUS_LABELS] || subTask.status}</span>
                  </div>
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={() => openViewModal(subTask)}>
                  View
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    )
  }

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey)
      } else {
        newSet.add(groupKey)
      }
      return newSet
    })
  }

  const toggleTaskDetails = (taskId: string) => {
    setExpandedTaskId((current) => {
      if (current === taskId) {
        setTaskDetailsPosition(null)
        return null
      }

      return taskId
    })
  }

  const setTaskDetailsToggleRef = (taskId: string, node: HTMLButtonElement | null) => {
    if (node) {
      taskDetailsToggleRefs.current.set(taskId, node)
      return
    }

    taskDetailsToggleRefs.current.delete(taskId)
  }

  const setActiveGrouping = (group: 'status' | 'priority' | 'assigned_to' | 'related_app') => {
    const isAlreadyActive =
      (group === 'status' && groupByStatus) ||
      (group === 'priority' && groupByPriority) ||
      (group === 'assigned_to' && groupByAssignedTo) ||
      (group === 'related_app' && groupByRelatedApp)

    setGroupByStatus(group === 'status' ? !isAlreadyActive : false)
    setGroupByPriority(group === 'priority' ? !isAlreadyActive : false)
    setGroupByAssignedTo(group === 'assigned_to' ? !isAlreadyActive : false)
    setGroupByRelatedApp(group === 'related_app' ? !isAlreadyActive : false)
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
      return
    }

    setSortField(field)
    setSortDirection(field === 'due' ? 'asc' : 'asc')
  }

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return null
    }

    return sortDirection === 'asc' ? '↑' : '↓'
  }

  // Group tasks if needed
  const groupedTasks = useMemo(() => {
      if (!groupByStatus && !groupByPriority && !groupByAssignedTo && !groupByRelatedApp) {
      return null
    }

    const grouped: Record<string, Task[]> = {}

    if (groupByStatus) {
      selectableStatuses.forEach((status) => {
        grouped[status] = tasks.filter((t) => t.status === status)
      })
      } else if (groupByAssignedTo) {
        // Group by assigned user or 'Unassigned'
        const unassignedTasks = tasks.filter((t) => !t.assigned_to)
        if (unassignedTasks.length > 0) {
          grouped['Unassigned'] = unassignedTasks
        }
        const assignedTasks = tasks.filter((t) => t.assigned_to)
        assignedTasks.forEach((task) => {
          const userName = assigneeLabelMap.get(task.assigned_to!) || task.assigned_to || 'Unknown'
          if (!grouped[userName]) {
            grouped[userName] = []
          }
          grouped[userName].push(task)
        })
      } else if (groupByRelatedApp) {
        Array.from(new Set(tasks.map((task) => task.related_app).filter(Boolean)))
          .sort((left, right) => left.localeCompare(right))
          .forEach((app) => {
            grouped[app] = tasks.filter((t) => t.related_app === app)
          })
      } else if (groupByPriority) {
      TASK_PRIORITIES.forEach((priority) => {
        grouped[priority] = tasks.filter((t) => t.priority === priority)
      })
    }

    return grouped
  }, [groupByStatus, groupByPriority, groupByAssignedTo, groupByRelatedApp, tasks, assigneeLabelMap, selectableStatuses])
  const expandedTask = expandedTaskId ? allTasks.find((task) => task.id === expandedTaskId) || null : null
  const expandedTaskCreatedByName = expandedTask?.created_by ? assigneeLabelMap.get(expandedTask.created_by) || 'Unknown user' : 'Unknown user'
  const expandedTaskParentTitle = expandedTask?.parent_task_id
    ? allTasks.find((task) => task.id === expandedTask.parent_task_id)?.title || 'Parent task'
    : null
  const prerequisiteByTaskId = useMemo(() => {
    const entries = taskPrerequisitesQuery.data || []
    return new Map(entries.map((item) => [item.task_id, item.prerequisite_task_id]))
  }, [taskPrerequisitesQuery.data])
  const subTasksByParentId = useMemo(() => {
    const grouped = new Map<string, Task[]>()
    for (const task of allTasks) {
      if (!task.parent_task_id) continue
      const list = grouped.get(task.parent_task_id) || []
      list.push(task)
      grouped.set(task.parent_task_id, list)
    }

    for (const [, list] of grouped) {
      list.sort((left, right) => {
        const leftOrder = left.sub_task_order ?? Number.MAX_SAFE_INTEGER
        const rightOrder = right.sub_task_order ?? Number.MAX_SAFE_INTEGER
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder
        }
        return left.created_at.localeCompare(right.created_at)
      })
    }

    return grouped
  }, [allTasks])
  const isSubtaskReady = (task: Task) => {
    if (!task.parent_task_id) {
      return null
    }

    const prerequisiteTaskId = prerequisiteByTaskId.get(task.id)
    if (!prerequisiteTaskId) {
      return true
    }

    const prerequisiteTask = allTasks.find((item) => item.id === prerequisiteTaskId)
    return prerequisiteTask?.status === 'released_to_dev'
  }
  const renderReadinessBadge = (task: Task) => {
    const readiness = isSubtaskReady(task)
    if (readiness === null) {
      return null
    }

    return readiness ? (
      <span className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800">
        ✓ Ready
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
        ✗ Blocked
      </span>
    )
  }
  const handleExportVisibleTasks = () => {
    const today = new Date().toISOString().slice(0, 10)
    exportTasksToExcel(tasks, assigneeLabelMap, `tasks-${today}.xlsx`)
  }
  const canEditSupportTicket = profile?.role === 'admin' || profile?.role === 'support'

  return (
    <div className="space-y-6">
      <TaskApprovalScreen
        isTaskApprover={Boolean(profile?.is_task_approver)}
        tasks={newRequestTaskList}
        relatedAppResponsiblePersonMap={relatedAppResponsiblePersonMap}
        profileMap={assigneeLabelMap}
        assigneeOptions={reviewAssigneeOptions}
        activeTaskId={reviewActionTaskId}
        autoOpen={shouldAutoOpenReview}
        onAutoOpenHandled={() => setShouldAutoOpenReview(false)}
        onApprove={(task, assigneeId) => handleReviewStatusChange(task, 'approved', assigneeId)}
        onReject={(task) => handleReviewStatusChange(task, 'rejected')}
      />

      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
            <p className="text-sm text-gray-600">Track requests, delivery status, release progress, and task notes.</p>
          </div>
          <div className="flex-1 md:max-w-md">
            <input
              type="text"
              placeholder="Search by title, status, priority, type, app, assigned to, requested by..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Unified Filter & Grouping Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeCompleted}
              onChange={(event) => setIncludeCompleted(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium">Include Completed</span>
          </label>

          <div className="h-8 w-0.5 bg-stone-400"></div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">Due by</span>
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              value={overdueFilter}
              onChange={(event) => setOverdueFilter(event.target.value as OverdueFilter)}
            >
              <option value="all">All</option>
              <option value="any_overdue">Any Overdue</option>
              <option value="status_overdue">Status Overdue</option>
              <option value="task_overdue">Task Overdue</option>
              <option value="both_overdue">Both Overdue</option>
              <option value="not_overdue">Not Overdue</option>
            </select>
          </label>

          <div className="h-8 w-0.5 bg-stone-400"></div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">Assigned to</span>
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              value={unassignedFilter}
              onChange={(event) => setUnassignedFilter(event.target.value as UnassignedFilter)}
            >
              <option value="all">Any Assignee</option>
              <option value="yes">Unassigned</option>
              <option value="no">Assigned</option>
              {assignedToFilterOptions.map((assignee) => (
                <option key={assignee.id} value={`user:${assignee.id}`}>
                  {assignee.label}
                </option>
              ))}
            </select>
          </label>

          {/* Visual Separator */}
          <div className="h-8 w-0.5 bg-stone-400"></div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">Group by</span>
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              value={groupByStatus ? 'status' : groupByPriority ? 'priority' : groupByAssignedTo ? 'assigned_to' : groupByRelatedApp ? 'related_app' : 'none'}
              onChange={(event) => {
                const nextGroup = event.target.value
                if (nextGroup === 'none') {
                  setGroupByStatus(false)
                  setGroupByPriority(false)
                  setGroupByAssignedTo(false)
                  setGroupByRelatedApp(false)
                  return
                }

                setActiveGrouping(nextGroup as 'status' | 'priority' | 'assigned_to' | 'related_app')
              }}
            >
              <option value="none">None</option>
              <option value="status">Status</option>
              <option value="priority">Priority</option>
              <option value="assigned_to">Assignee</option>
              <option value="related_app">Related App</option>
            </select>
          </label>

          {/* Move the Show all/mine toggle to the end */}

          {/* Spacer to push counter and Create button to the right */}
          <div className="flex-1"></div>

          {/* Counter */}
          <div className="rounded-lg border border-stone-300 bg-stone-100 px-4 py-2 text-sm font-medium text-slate-700">
            {openTasks} open / {totalTasks} total
          </div>

          <Button variant="secondary" onClick={handleExportVisibleTasks} size="sm">
            Export to Excel
          </Button>

          {/* Create Task Button */}
          <Button variant="primary" onClick={openCreateModal} size="sm">
            Create Task
          </Button>

          {/* Show all/mine toggle at the end */}
          <Button
            variant={myTasksOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMyTasksOnly((current) => !current)}
          >
            {isSalesLikeRole(profile?.role)
              ? myTasksOnly
                ? '✓ My Created Tasks'
                : 'Show All Tasks'
              : myTasksOnly
                ? '✓ My Tasks Only'
                : 'Show All Tasks'}
          </Button>
        </div>
      </div>

      {pageError && (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {pageError instanceof Error ? pageError.message : 'Unable to load tasks.'}
          </div>
        </Card>
      )}

      {/* Search and Grouping Controls - now integrated into header above */}

      <Card header={<h2 className="font-semibold text-gray-900">All Tasks</h2>}>
        {tasksQuery.isLoading || profilesQuery.isLoading ? (
          <div className="flex min-h-[220px] items-center justify-center">
            <LoadingSpinner text="Loading tasks..." />
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 text-center">
            <div>
              <p className="text-base font-medium text-gray-900">No tasks yet</p>
              <p className="text-sm text-gray-500">Create the first task to start populating the board and dashboard.</p>
            </div>
            <Button onClick={openCreateModal}>Create Task</Button>
          </div>
        ) : groupedTasks ? (
          // Grouped View with Accordions
          <div className="space-y-2">
            {Object.entries(groupedTasks).map(([groupKey, groupTasks]) => {
              const isExpanded = expandedGroups.has(groupKey)
              const label = groupByStatus 
                ? TASK_STATUS_LABELS[groupKey as keyof typeof TASK_STATUS_LABELS] || groupKey
                : groupByPriority
                  ? TASK_PRIORITY_LABELS[groupKey as keyof typeof TASK_PRIORITY_LABELS] || groupKey
                  : groupKey
              const color = groupByStatus
                ? TASK_STATUS_COLORS[groupKey as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
                : groupByPriority
                  ? TASK_PRIORITY_COLORS[groupKey as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
                  : '#6b7280'
                const displayLabel = groupByAssignedTo || groupByRelatedApp ? groupKey : label
                const displayColor = groupByAssignedTo || groupByRelatedApp ? '#6b7280' : color

              return (
                <div key={groupKey} className="rounded-lg border border-gray-200">
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
                      <span
                        className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium"
                          style={{ borderColor: displayColor, color: displayColor }}
                      >
                          {displayLabel}
                      </span>
                      <span className="text-sm text-gray-600">({groupTasks.length})</span>
                    </div>
                  </button>
                  {isExpanded && groupTasks.length > 0 && (
                    <div className="overflow-x-auto border-t border-gray-200">
                      <table className="w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                            <th className="px-4 py-3">Title</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Priority</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Requested By</th>
                            <th className="px-4 py-3">Support Ticket</th>
                            <th className="px-4 py-3">Created By</th>
                            <th className="px-4 py-3">Assignee</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {groupTasks.map((task) => {
                            const statusColor = TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
                            const priorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
                            const createdByName = task.created_by ? assigneeLabelMap.get(task.created_by) || 'Unknown user' : 'Unknown user'
                            const assigneeName = profilesQuery.data?.find(p => p.id === task.assigned_to)?.full_name

                            return (
                              <tr key={task.id} className="align-top hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="space-y-1">
                                    {task.parent_task_id && (
                                      <p className="text-xs text-amber-600 font-medium">→ Sub-task</p>
                                    )}
                                    <div className="flex items-start justify-between gap-3">
                                      <p className={clsx('font-medium text-gray-900', task.parent_task_id && 'ml-2')}>{task.title}</p>
                                      <div className="flex items-center gap-2">
                                        {renderReadinessBadge(task)}
                                        {isTaskStatusOverdue(task) && (
                                          <span className="shrink-0 rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                                            Status: Overdue
                                          </span>
                                        )}
                                        {isTaskOverallDueOverdue(task) && (
                                          <span className="shrink-0 rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                                            Task: Overdue
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <p className="max-w-md text-gray-500">
                                      {truncateText(stripHtml(task.description || 'No description provided.'), 96)}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-4 py-4">
                                  {inlineEditTaskId === task.id && inlineEditField === 'status' ? (
                                    <div className="flex items-center gap-2">
                                      <select
                                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                        value={inlineEditValue}
                                        onChange={(e) => setInlineEditValue(e.target.value)}
                                        autoFocus
                                      >
                                        {selectableStatuses.map((status) => (
                                          <option key={status} value={status}>
                                            {TASK_STATUS_LABELS[status]}
                                          </option>
                                        ))}
                                      </select>
                                      <Button size="sm" variant="primary" onClick={handleInlineEditSave} isLoading={updateTaskMutation.isPending}>
                                        ✓
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={closeInlineEdit}>
                                        ✕
                                      </Button>
                                    </div>
                                  ) : (
                                    <span
                                      className={`${badgeClassName} cursor-pointer hover:opacity-80 transition`}
                                      style={{ borderColor: statusColor, color: statusColor }}
                                      onClick={() => openInlineEdit(task.id, 'status', task.status)}
                                    >
                                      {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-4">
                                  {inlineEditTaskId === task.id && inlineEditField === 'priority' ? (
                                    <div className="flex items-center gap-2">
                                      <select
                                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                        value={inlineEditValue}
                                        onChange={(e) => setInlineEditValue(e.target.value)}
                                        autoFocus
                                      >
                                        {TASK_PRIORITIES.map((priority) => (
                                          <option key={priority} value={priority}>
                                            {TASK_PRIORITY_LABELS[priority]}
                                          </option>
                                        ))}
                                      </select>
                                      <Button size="sm" variant="primary" onClick={handleInlineEditSave} isLoading={updateTaskMutation.isPending}>
                                        ✓
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={closeInlineEdit}>
                                        ✕
                                      </Button>
                                    </div>
                                  ) : (
                                    <span
                                      className={`${badgeClassName} cursor-pointer hover:opacity-80 transition`}
                                      style={{ borderColor: priorityColor, color: priorityColor }}
                                      onClick={() => openInlineEdit(task.id, 'priority', task.priority)}
                                    >
                                      {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-gray-700">
                                  {TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}
                                </td>
                                <td className="px-4 py-4 text-gray-700">{task.related_app}</td>
                                <td className="px-4 py-4 text-gray-700">{task.requested_by}</td>
                                <td className="px-4 py-4 text-gray-700">{task.support_ticket_no || '-'}</td>
                                <td className="px-4 py-4 text-gray-700">{createdByName}</td>
                                <td className="px-4 py-4 text-gray-700">
                                  {assigneeName || 'Unassigned'}
                                  {!assigneeName && (
                                    <div className="mt-2">
                                      <Button variant="secondary" size="sm" onClick={() => openAssignModal(task)}>
                                        Assign
                                      </Button>
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-gray-700">
                                  {getTaskCurrentStatusDueDate(task) ? formatDate(getTaskCurrentStatusDueDate(task)!) : 'Not set'}
                                </td>
                                <td className="px-4 py-4 text-gray-700 text-center">
                                  {task.estimated_hours ? `${task.estimated_hours}h` : '-'}
                                </td>
                                <td className="px-4 py-4 text-gray-700 text-sm">
                                  {task.parent_task_id ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 border border-amber-200 text-amber-700 font-medium">
                                      → {tasks.find(t => t.id === task.parent_task_id)?.title || 'Parent task'}
                                    </span>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td className="px-4 py-4 text-gray-700">
                                  {task.completed_at ? formatDateTime(task.completed_at) : 'Not completed'}
                                </td>
                                <td className="px-4 py-4 text-gray-500">{formatRelativeTime(task.updated_at)}</td>
                                <td className="py-4 pl-4">
                                  {isSalesLikeRole(profile?.role) ? (
                                    <div className="flex justify-end gap-2">
                                      <Button variant="secondary" size="sm" onClick={() => openViewModal(task)}>
                                        View
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end gap-2">
                                      {isSalesLikeRole(profile?.role) ? (
                                        <Button variant="secondary" size="sm" onClick={() => openViewModal(task)}>
                                          View
                                        </Button>
                                      ) : (
                                        <>
                                          {task.type === 'new_feature' && !task.parent_task_id && (
                                            <Button variant="ghost" size="sm" onClick={() => openCreateSubTaskModal(task)}>
                                              Add Sub-task
                                            </Button>
                                          )}
                                          <Button variant="secondary" size="sm" onClick={() => openEditModal(task)}>
                                            Edit
                                          </Button>
                                        </>
                                      )}
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDelete(task)}
                                        isLoading={deleteTaskMutation.isPending && deleteTaskMutation.variables === task.id}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          // Table View
          <div className="w-full rounded-lg border border-gray-200" style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
            <table className="w-full divide-y divide-gray-200 text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: '#f5f3ff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="bg-stone-100 px-4 py-3">
                    <div className="space-y-2">
                      <button
                        className="flex items-center gap-1 rounded px-2 py-1 font-semibold text-gray-700 hover:bg-gray-100"
                        onClick={() => toggleSort('title')}
                      >
                        Task {getSortIndicator('title')}
                      </button>
                      <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-6">
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('status')}>
                          Status {getSortIndicator('status')}
                        </button>
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('priority')}>
                          Priority {getSortIndicator('priority')}
                        </button>
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('assigned_to')}>
                          Assigned To {getSortIndicator('assigned_to')}
                        </button>
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('type')}>
                          Type {getSortIndicator('type')}
                        </button>
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('related_app')}>
                          App {getSortIndicator('related_app')}
                        </button>
                        <button className="rounded px-2 py-1 text-left font-semibold text-gray-600 hover:bg-gray-100" onClick={() => toggleSort('due')}>
                          Due {getSortIndicator('due')}
                        </button>
                      </div>
                    </div>
                  </th>
                  <th className="bg-stone-100 py-3 pl-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.map((task) => {
                  const statusColor = TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
                  const priorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
                  const isDetailsOpen = expandedTaskId === task.id
                  const taskDescription = truncateText(stripHtml(task.description || 'No description provided.'), 150)

                  return (
                    <tr key={task.id} className={clsx('align-top', task.parent_task_id && 'bg-amber-50/50 border-l-4 border-l-amber-300')}>
                      <td className="px-4 py-4">
                        <div className="space-y-3">
                          {task.parent_task_id && (
                            <p className="text-xs text-amber-600 font-medium">→ Sub-task</p>
                          )}
                          <div className="flex items-start gap-3">
                            <p className={clsx('min-w-0 flex-1 text-sm text-gray-600', task.parent_task_id && 'ml-2')}>
                              <span className="font-semibold text-gray-900">{task.title}</span>
                              <span className="ml-3">{taskDescription}</span>
                            </p>
                            <div className="flex items-center gap-2">
                              {renderReadinessBadge(task)}
                              {isTaskStatusOverdue(task) && (
                                <span className="shrink-0 rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                                  Status: Overdue
                                </span>
                              )}
                              {isTaskOverallDueOverdue(task) && (
                                <span className="shrink-0 rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                                  Task: Overdue
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs text-gray-600 sm:grid-cols-6">
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Status</p>
                              {inlineEditTaskId === task.id && inlineEditField === 'status' ? (
                                <div className="flex items-center gap-2">
                                  <select
                                    className="min-w-0 rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                    value={inlineEditValue}
                                    onChange={(e) => setInlineEditValue(e.target.value)}
                                    autoFocus
                                  >
                                    {selectableStatuses.map((status) => (
                                      <option key={status} value={status}>
                                        {TASK_STATUS_LABELS[status]}
                                      </option>
                                    ))}
                                  </select>
                                  <Button size="sm" variant="primary" onClick={handleInlineEditSave} isLoading={updateTaskMutation.isPending}>
                                    ✓
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={closeInlineEdit}>
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <span
                                  className={`${badgeClassName} cursor-pointer hover:opacity-80 transition`}
                                  style={{ borderColor: statusColor, color: statusColor }}
                                  onClick={() => openInlineEdit(task.id, 'status', task.status)}
                                >
                                  {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Priority</p>
                              {inlineEditTaskId === task.id && inlineEditField === 'priority' ? (
                                <div className="flex items-center gap-2">
                                  <select
                                    className="min-w-0 rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                    value={inlineEditValue}
                                    onChange={(e) => setInlineEditValue(e.target.value)}
                                    autoFocus
                                  >
                                    {TASK_PRIORITIES.map((priority) => (
                                      <option key={priority} value={priority}>
                                        {TASK_PRIORITY_LABELS[priority]}
                                      </option>
                                    ))}
                                  </select>
                                  <Button size="sm" variant="primary" onClick={handleInlineEditSave} isLoading={updateTaskMutation.isPending}>
                                    ✓
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={closeInlineEdit}>
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <span
                                  className={`${badgeClassName} cursor-pointer hover:opacity-80 transition`}
                                  style={{ borderColor: priorityColor, color: priorityColor }}
                                  onClick={() => openInlineEdit(task.id, 'priority', task.priority)}
                                >
                                  {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Assigned To</p>
                              <div className="text-sm text-gray-700">
                                {task.assigned_to ? assigneeLabelMap.get(task.assigned_to) || 'Unknown user' : 'Unassigned'}
                              </div>
                              {!task.assigned_to && (
                                <div className="mt-2">
                                  <Button variant="secondary" size="sm" onClick={() => openAssignModal(task)}>
                                    Assign
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Type</p>
                              <div className="text-sm text-gray-700">{TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}</div>
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">App</p>
                              <div className="text-sm text-gray-700">{task.related_app || 'None'}</div>
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Due</p>
                              <div className="text-sm text-gray-700">{getTaskCurrentStatusDueDate(task) ? formatDate(getTaskCurrentStatusDueDate(task)!) : 'Not set'}</div>
                            </div>
                            <div className="min-w-0">
                              <p className="mb-1 font-medium uppercase tracking-wide text-gray-500">Support Ticket</p>
                              <div className="text-sm text-gray-700">{task.support_ticket_no || 'Not set'}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="relative py-4 pl-4 align-top">
                        {isSalesLikeRole(profile?.role) ? (
                          <div className="flex justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => openViewModal(task)}>
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleTaskDetails(task.id)}
                              ref={(node) => setTaskDetailsToggleRef(task.id, node)}
                              data-task-details-toggle={task.id}
                            >
                              {isDetailsOpen ? '▲' : '▼'}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => openEditModal(task)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleTaskDetails(task.id)}
                              ref={(node) => setTaskDetailsToggleRef(task.id, node)}
                              data-task-details-toggle={task.id}
                            >
                              {isDetailsOpen ? '▲' : '▼'}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {expandedTask && taskDetailsPosition && createPortal(
        <div
          className="fixed z-40 rounded-xl border border-stone-300 bg-white p-4 shadow-2xl"
          style={{ top: taskDetailsPosition.top, left: taskDetailsPosition.left, width: taskDetailsPosition.width }}
          data-task-details-panel={expandedTask.id}
        >
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Requested By</p>
              <p className="mt-1">{expandedTask.requested_by || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Created By</p>
              <p className="mt-1">{expandedTaskCreatedByName}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Estimated Hours</p>
              <p className="mt-1">{expandedTask.estimated_hours ? `${expandedTask.estimated_hours}h` : 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Parent Task</p>
              <p className="mt-1">{expandedTaskParentTitle || 'None'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Completed</p>
              <p className="mt-1">{expandedTask.completed_at ? formatDateTime(expandedTask.completed_at) : 'Not completed'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Updated</p>
              <p className="mt-1">{formatRelativeTime(expandedTask.updated_at)}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Client Priority</p>
              <p className="mt-1">{TASK_PRIORITY_LABELS[expandedTask.client_priority as keyof typeof TASK_PRIORITY_LABELS] || expandedTask.client_priority || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Billable</p>
              <p className="mt-1">{expandedTask.billable ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Release Version</p>
              <p className="mt-1">{expandedTask.release_version || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Release Date</p>
              <p className="mt-1">{expandedTask.release_date ? formatDate(expandedTask.release_date) : 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Support Ticket No</p>
              <p className="mt-1">{expandedTask.support_ticket_no || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Current Status Due Date</p>
              <p className="mt-1">{getTaskCurrentStatusDueDate(expandedTask) ? formatDate(getTaskCurrentStatusDueDate(expandedTask)!) : 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Overall Due Date</p>
              <p className="mt-1">{expandedTask.overall_due_date ? formatDate(expandedTask.overall_due_date) : 'Not set'}</p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDelete(expandedTask)}
              isLoading={deleteTaskMutation.isPending && deleteTaskMutation.variables === expandedTask.id}
            >
                Delete
            </Button>
          </div>
        </div>,
        document.body
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col overflow-hidden rounded-xl border border-stone-300 bg-stone-100 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isViewOnly ? 'Task Details' : editingTask ? 'Edit Task' : 'Create Task'}
                </h2>
                <p className="text-sm text-gray-500">
                  {isViewOnly
                    ? 'Review request details, delivery status, and task notes.'
                    : 'Capture request details, delivery status, release notes, and updates.'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {parentTask && (
                  <Button type="button" variant="secondary" size="sm" onClick={() => openViewModal(parentTask)}>
                    Parent Task: {parentTask.title}
                  </Button>
                )}
                <button className="text-gray-500 hover:text-gray-700" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>

            <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{formError}</div>
              )}


              {/* Testing controls for in_dev_testing and in_2nd_dev_testing */}
              {editingTask && (['in_dev_testing', 'in_2nd_dev_testing'].includes(editingTask.status)) && profile && (
                <div className="mb-4 flex flex-col gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
                  <span className="font-semibold">Testing Results</span>
                  {/* First Test Pill Checkboxes */}
                  <div className="flex items-center gap-2">
                    <span>First Test:</span>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full border text-xs font-semibold ${editingTask.first_test === 'passed' ? 'bg-green-200 border-green-500 text-green-900' : 'bg-white border-gray-300 text-gray-700'} ${profile.is_tester ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                      disabled={!profile.is_tester}
                      onClick={async () => {
                        if (!profile.is_tester) return;
                        const newVal = editingTask.first_test === 'passed' ? null : 'passed';
                        await updateTaskMutation.mutateAsync({
                          taskId: editingTask.id,
                          updates: { first_test: newVal },
                        });
                        if (editingTask.first_test !== newVal) {
                          await createTaskNoteMutation.mutateAsync({
                            task_id: editingTask.id,
                            content: `<p><strong>First Test</strong> set to <strong>${newVal ? newVal : 'unset'}</strong> by ${profile.full_name || profile.email}</p>`,
                            created_by: profile.id,
                          });
                        }
                      }}
                    >
                      Passed
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full border text-xs font-semibold ${editingTask.first_test === 'failed' ? 'bg-red-200 border-red-500 text-red-900' : 'bg-white border-gray-300 text-gray-700'} ${profile.is_tester ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                      disabled={!profile.is_tester}
                      onClick={async () => {
                        if (!profile.is_tester) return;
                        const newVal = editingTask.first_test === 'failed' ? null : 'failed';
                        await updateTaskMutation.mutateAsync({
                          taskId: editingTask.id,
                          updates: { first_test: newVal },
                        });
                        if (editingTask.first_test !== newVal) {
                          await createTaskNoteMutation.mutateAsync({
                            task_id: editingTask.id,
                            content: `<p><strong>First Test</strong> set to <strong>${newVal ? newVal : 'unset'}</strong> by ${profile.full_name || profile.email}</p>`,
                            created_by: profile.id,
                          });
                        }
                      }}
                    >
                      Failed
                    </button>
                    {editingTask.first_test && (
                      <span className={editingTask.first_test === 'passed' ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                        {editingTask.first_test.charAt(0).toUpperCase() + editingTask.first_test.slice(1)}
                      </span>
                    )}
                  </div>
                  {/* Second Test Pill Checkboxes */}
                  <div className="flex items-center gap-2">
                    <span>Second Test:</span>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full border text-xs font-semibold ${editingTask.second_test === 'passed' ? 'bg-green-200 border-green-500 text-green-900' : 'bg-white border-gray-300 text-gray-700'} ${profile.is_2nd_tester ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                      disabled={!profile.is_2nd_tester}
                      onClick={async () => {
                        if (!profile.is_2nd_tester) return;
                        const newVal = editingTask.second_test === 'passed' ? null : 'passed';
                        await updateTaskMutation.mutateAsync({
                          taskId: editingTask.id,
                          updates: { second_test: newVal },
                        });
                        if (editingTask.second_test !== newVal) {
                          await createTaskNoteMutation.mutateAsync({
                            task_id: editingTask.id,
                            content: `<p><strong>Second Test</strong> set to <strong>${newVal ? newVal : 'unset'}</strong> by ${profile.full_name || profile.email}</p>`,
                            created_by: profile.id,
                          });
                        }
                      }}
                    >
                      Passed
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full border text-xs font-semibold ${editingTask.second_test === 'failed' ? 'bg-red-200 border-red-500 text-red-900' : 'bg-white border-gray-300 text-gray-700'} ${profile.is_2nd_tester ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                      disabled={!profile.is_2nd_tester}
                      onClick={async () => {
                        if (!profile.is_2nd_tester) return;
                        const newVal = editingTask.second_test === 'failed' ? null : 'failed';
                        await updateTaskMutation.mutateAsync({
                          taskId: editingTask.id,
                          updates: { second_test: newVal },
                        });
                        if (editingTask.second_test !== newVal) {
                          await createTaskNoteMutation.mutateAsync({
                            task_id: editingTask.id,
                            content: `<p><strong>Second Test</strong> set to <strong>${newVal ? newVal : 'unset'}</strong> by ${profile.full_name || profile.email}</p>`,
                            created_by: profile.id,
                          });
                        }
                      }}
                    >
                      Failed
                    </button>
                    {editingTask.second_test && (
                      <span className={editingTask.second_test === 'passed' ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                        {editingTask.second_test.charAt(0).toUpperCase() + editingTask.second_test.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isViewOnly && editingTask ? (
                <div className="grid gap-6 xl:grid-cols-2">
                  <section className={modalSectionClassName}>
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Information</h3>
                      <p className="text-sm text-gray-500">Request overview</p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <p className={detailLabelClassName}>Added By</p>
                        <p className={detailValueClassName}>{createdByName}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Requested By</p>
                        <p className={detailValueClassName}>{editingTask.requested_by || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Support Ticket No</p>
                        <p className={detailValueClassName}>{editingTask.support_ticket_no || 'Not set'}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Overall Due Date</p>
                        <p className={detailValueClassName}>{editingTask.overall_due_date ? formatDate(editingTask.overall_due_date) : 'Not set'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className={detailLabelClassName}>Title</p>
                        <p className="text-base font-medium text-gray-900">{editingTask.title}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className={detailLabelClassName}>Description</p>
                        <div className="mt-2 rounded-lg border border-stone-200 bg-white/80 p-4 text-sm text-gray-700">
                          <RichTextContent html={editingTask.description || ''} emptyText="No description provided." />
                        </div>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Billable</p>
                        <p className={detailValueClassName}>{editingTask.billable ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </section>

                  <section className={modalSectionClassName}>
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Additional Information</h3>
                      <p className="text-sm text-gray-500">Assignment and delivery details</p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <p className={detailLabelClassName}>Assigned To</p>
                        <p className={detailValueClassName}>{assignedToName}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Status</p>
                        <span
                          className={badgeClassName}
                          style={{ borderColor: TASK_STATUS_COLORS[editingTask.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280', color: TASK_STATUS_COLORS[editingTask.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280' }}
                        >
                          {TASK_STATUS_LABELS[editingTask.status as keyof typeof TASK_STATUS_LABELS] || editingTask.status}
                        </span>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Client Priority</p>
                        <span
                          className={badgeClassName}
                          style={{ borderColor: TASK_PRIORITY_COLORS[editingTask.client_priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280', color: TASK_PRIORITY_COLORS[editingTask.client_priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280' }}
                        >
                          {TASK_PRIORITY_LABELS[editingTask.client_priority as keyof typeof TASK_PRIORITY_LABELS] || editingTask.client_priority}
                        </span>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Priority</p>
                        <span
                          className={badgeClassName}
                          style={{ borderColor: TASK_PRIORITY_COLORS[editingTask.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280', color: TASK_PRIORITY_COLORS[editingTask.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280' }}
                        >
                          {TASK_PRIORITY_LABELS[editingTask.priority as keyof typeof TASK_PRIORITY_LABELS] || editingTask.priority}
                        </span>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Related App</p>
                        <p className={detailValueClassName}>{editingTask.related_app}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Type</p>
                        <p className={detailValueClassName}>{TASK_TYPE_LABELS[editingTask.type as keyof typeof TASK_TYPE_LABELS] || editingTask.type}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Estimated Hours</p>
                        <p className={detailValueClassName}>{editingTask.estimated_hours ? `${editingTask.estimated_hours}h` : 'Not set'}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Current Status Due Date</p>
                        <p className={detailValueClassName}>
                          {getTaskCurrentStatusDueDate(editingTask) ? formatDate(getTaskCurrentStatusDueDate(editingTask)!) : 'Not set'}
                        </p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Release Date</p>
                        <p className={detailValueClassName}>{editingTask.release_date ? formatDate(editingTask.release_date) : 'Not set'}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Release Version</p>
                        <p className={detailValueClassName}>{editingTask.release_version || 'Not set'}</p>
                      </div>
                      <div>
                        <p className={detailLabelClassName}>Completed At</p>
                        <p className={detailValueClassName}>{editingTask.completed_at ? formatDateTime(editingTask.completed_at) : 'Not completed yet'}</p>
                      </div>
                      {editingTask.parent_task_id && (
                        <>
                          <div>
                            <p className={detailLabelClassName}>Prerequisite Task</p>
                            <p className={detailValueClassName}>
                              {(() => {
                                const prerequisiteTaskId = prerequisiteByTaskId.get(editingTask.id)
                                if (!prerequisiteTaskId) {
                                  return 'None'
                                }
                                return allTasks.find((task) => task.id === prerequisiteTaskId)?.title || 'Unknown task'
                              })()}
                            </p>
                          </div>
                          <div>
                            <p className={detailLabelClassName}>Ready To Start</p>
                            <div className="mt-1">{renderReadinessBadge(editingTask)}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </section>

                  {renderSubTasksSection(editingTask)}

                  {renderAttachmentsSection()}

                  <section className={clsx(modalSectionClassName, 'xl:col-span-2')}>
                    <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Notes</h3>
                        <p className="text-sm text-gray-500">Notes and activity</p>
                      </div>
                      <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2">
                        <div>
                          <span className="font-medium text-gray-700">Created:</span> {formatDateTime(editingTask.created_at)}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Updated:</span> {formatDateTime(editingTask.updated_at)}
                        </div>
                      </div>
                    </div>

                    {taskNotesQuery.isLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <LoadingSpinner text="Loading notes..." />
                      </div>
                    ) : notes.length === 0 ? (
                      <p className="text-sm text-gray-500">No notes yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {notes.map((note) => (
                          <div key={note.id} className="rounded-lg border border-gray-200 bg-white/80 p-4">
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-900">{note.author_name}</p>
                              <p className="text-xs text-gray-500">{formatDateTime(note.created_at)}</p>
                            </div>
                            <RichTextContent html={note.content} />
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              ) : (
                <div className="grid gap-6 xl:grid-cols-2">
                  <section className={modalSectionClassName}>
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Information</h3>
                      <p className="text-sm text-gray-500">Request overview</p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <p className={detailLabelClassName}>Added By</p>
                        <p className={detailValueClassName}>{createdByName}</p>
                      </div>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Requested By</span>
                        <input
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ring-0 focus:border-blue-500"
                          value={formValues.requested_by}
                          onChange={(event) => setFormValues((current) => ({ ...current, requested_by: event.target.value }))}
                          placeholder="Jane Smith"
                        />
                      </label>

                      {canEditSupportTicket && !formValues.parent_task_id ? (
                        <label className="space-y-2">
                          <span className="text-sm font-medium text-gray-700">Support Ticket No</span>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ring-0 focus:border-blue-500"
                            value={formValues.support_ticket_no}
                            onChange={(event) =>
                              setFormValues((current) => ({ ...current, support_ticket_no: event.target.value }))
                            }
                            placeholder="e.g. SUP-1024"
                          />
                        </label>
                      ) : null}

                      {profile?.role === 'admin' ? (
                        <label className="space-y-2">
                          <span className="text-sm font-medium text-gray-700">Overall Due Date</span>
                          <input
                            type="date"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ring-0 focus:border-blue-500"
                            value={formValues.overall_due_date}
                            onChange={(event) =>
                              setFormValues((current) => ({ ...current, overall_due_date: event.target.value }))
                            }
                          />
                        </label>
                      ) : (
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-700">Overall Due Date</span>
                          <div className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600">
                            {formValues.overall_due_date ? formatDate(formValues.overall_due_date) : 'Not set'}
                          </div>
                        </div>
                      )}

                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-medium text-gray-700">Title</span>
                        <input
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ring-0 focus:border-blue-500"
                          value={formValues.title}
                          onChange={(event) => setFormValues((current) => ({ ...current, title: event.target.value }))}
                          placeholder="Promote package to production"
                          required
                        />
                      </label>

                      <div className="md:col-span-2">
                        <RichTextEditor
                          label="Description"
                          value={formValues.description}
                          onChange={(value) => setFormValues((current) => ({ ...current, description: value }))}
                          placeholder="Add the context, acceptance criteria, or deployment notes."
                        />
                      </div>

                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-medium text-gray-700">Billable</span>
                        <div className="flex h-[42px] items-center rounded-lg border border-gray-300 bg-white px-3">
                          <input
                            id="task-billable"
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-slate-700 focus:ring-slate-500"
                            checked={formValues.billable}
                            onChange={(event) => setFormValues((current) => ({ ...current, billable: event.target.checked }))}
                          />
                          <label htmlFor="task-billable" className="ml-3 text-sm text-gray-700">
                            Mark this task as billable
                          </label>
                        </div>
                      </label>
                    </div>
                  </section>

                  <section className={modalSectionClassName}>
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Additional Information</h3>
                      <p className="text-sm text-gray-500">Assignment and delivery details</p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Assigned To</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.assigned_to}
                          onChange={(event) => setFormValues((current) => ({ ...current, assigned_to: event.target.value }))}
                        >
                          <option value="">Unassigned</option>
                          {profileOptions.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.full_name || item.email || 'Unknown user'}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Status</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.status}
                          disabled={!editingTask && profile?.role !== 'admin'}
                          onChange={(event) =>
                            setFormValues((current) => {
                              const nextStatus = event.target.value
                              const existingDueDate = current.status_due_dates[nextStatus] || ''
                              const autoDueDate = existingDueDate || getAutoStatusDueDate(nextStatus, statusDefaultDaysMap)

                              return {
                                ...current,
                                status: nextStatus,
                                projected_completion: autoDueDate,
                                status_due_dates: autoDueDate
                                  ? { ...current.status_due_dates, [nextStatus]: autoDueDate }
                                  : current.status_due_dates,
                              }
                            })
                          }
                        >
                          {selectableStatuses.map((status) => (
                            <option key={status} value={status}>
                              {TASK_STATUS_LABELS[status]}
                            </option>
                          ))}
                        </select>
                        {!editingTask && profile?.role !== 'admin' ? (
                          <p className="text-xs text-gray-500">New tasks always start as New and go through approval.</p>
                        ) : null}
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Client Priority</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.client_priority}
                          onChange={(event) => setFormValues((current) => ({ ...current, client_priority: event.target.value }))}
                        >
                          {TASK_PRIORITIES.map((priority) => (
                            <option key={priority} value={priority}>
                              {TASK_PRIORITY_LABELS[priority]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Priority</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.priority}
                          onChange={(event) => setFormValues((current) => ({ ...current, priority: event.target.value }))}
                        >
                          {TASK_PRIORITIES.map((priority) => (
                            <option key={priority} value={priority}>
                              {TASK_PRIORITY_LABELS[priority]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Related App</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.related_app_id}
                          onChange={(event) => setFormValues((current) => ({ ...current, related_app_id: event.target.value }))}
                        >
                          {relatedAppOptions.map((app) => (
                            <option key={app.id} value={app.id}>
                              {app.name}
                              {app.current_version ? ` | Current: ${app.current_version}` : ''}
                              {app.next_release_version ? ` | Next: ${app.next_release_version}` : ''}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Type</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.type}
                          onChange={(event) => setFormValues((current) => ({ ...current, type: event.target.value }))}
                        >
                          {TASK_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {TASK_TYPE_LABELS[type]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Estimated Hours</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          placeholder="e.g., 8.5"
                          value={formValues.estimated_hours}
                          onChange={(event) => setFormValues((current) => ({ ...current, estimated_hours: event.target.value }))}
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Due Date for Current Status</span>
                        <input
                          type="date"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={formValues.projected_completion}
                          onChange={(event) =>
                            setFormValues((current) => ({
                              ...current,
                              projected_completion: event.target.value,
                              status_due_dates: {
                                ...current.status_due_dates,
                                [current.status]: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Release Date</span>
                        <input
                          type="date"
                          className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                          disabled
                          readOnly
                          value={formValues.release_date}
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Release Version</span>
                        <input
                          type="text"
                          className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                          disabled
                          readOnly
                          placeholder="Not editable here"
                          value={formValues.release_version}
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Parent Task</span>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          disabled={formValues.type !== 'new_feature'}
                          value={formValues.parent_task_id}
                          onChange={(event) =>
                            setFormValues((current) => ({ ...current, parent_task_id: event.target.value }))
                          }
                        >
                          <option value="">None (this is a parent task)</option>
                          {tasksQuery.data
                            ?.filter((task) => task.type === 'new_feature' && task.id !== editingTask?.id)
                            .map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.title}
                              </option>
                            ))}
                        </select>
                      </label>

                        {formValues.parent_task_id && (
                          <label className="space-y-2 md:col-span-2">
                            <span className="text-sm font-medium text-gray-700">Prerequisite Sub-task</span>
                            <select
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              value={formValues.prerequisite_task_id}
                              onChange={(event) =>
                                setFormValues((current) => ({ ...current, prerequisite_task_id: event.target.value }))
                              }
                            >
                              <option value="">None</option>
                              {(subTasksByParentId.get(formValues.parent_task_id) || [])
                                .filter((task) => task.id !== editingTask?.id)
                                .map((task) => (
                                  <option key={task.id} value={task.id}>
                                    {(task.sub_task_order || 0) > 0 ? `${task.sub_task_order}. ` : ''}
                                    {task.title}
                                  </option>
                                ))}
                            </select>
                          </label>
                        )}
                    </div>
                  </section>

                  {editingTask ? renderSubTasksSection(editingTask) : null}

                  {renderAttachmentsSection()}

                  <section className={clsx(modalSectionClassName, 'xl:col-span-2')}>
                    <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Notes</h3>
                        <p className="text-sm text-gray-500">Notes and activity</p>
                      </div>
                      <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-3">
                        <div>
                          <span className="font-medium text-gray-700">Created:</span>{' '}
                          {editingTask?.created_at ? formatDateTime(editingTask.created_at) : 'Will be set on save'}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Updated:</span>{' '}
                          {editingTask?.updated_at ? formatDateTime(editingTask.updated_at) : 'Will be set on save'}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Completed:</span>{' '}
                          {editingTask?.completed_at ? formatDateTime(editingTask.completed_at) : 'Not completed yet'}
                        </div>
                      </div>
                    </div>

                    {!editingTask ? (
                      <p className="text-sm text-gray-500">Create the task first, then add timestamped notes.</p>
                    ) : (
                      <div className="space-y-4">
                        <RichTextEditor
                          label="Add Note"
                          value={newNoteContent}
                          onChange={setNewNoteContent}
                          placeholder="Add an update, release note, or testing outcome."
                          minHeightClassName="min-h-[120px]"
                        />

                        {noteError && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            {noteError}
                          </div>
                        )}

                        <div className="flex justify-end">
                          <Button type="button" onClick={handleAddNote} isLoading={createTaskNoteMutation.isPending}>
                            Add Note
                          </Button>
                        </div>

                        {taskNotesQuery.isLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <LoadingSpinner text="Loading notes..." />
                          </div>
                        ) : notes.length === 0 ? (
                          <p className="text-sm text-gray-500">No notes yet.</p>
                        ) : (
                          <div className="space-y-4">
                            {notes.map((note) => (
                              <div key={note.id} className="rounded-lg border border-gray-200 bg-white/80 p-4">
                                <div className="mb-3">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{note.author_name}</p>
                                    <p className="text-xs text-gray-500">{formatDateTime(note.created_at)}</p>
                                  </div>
                                </div>
                                <RichTextContent html={note.content} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                </div>
              )}

              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <Button variant="ghost" type="button" onClick={closeModal}>
                  {isViewOnly ? 'Close' : 'Cancel'}
                </Button>
                {editingTask && editingTask.type === 'new_feature' && !editingTask.parent_task_id && (
                  <Button type="button" variant="secondary" onClick={() => openCreateSubTaskModal(editingTask, true)}>
                    Add Sub-task
                  </Button>
                )}
                {!isViewOnly && (
                  <Button type="submit" isLoading={isSaving}>
                    {editingTask ? 'Save Changes' : 'Create Task'}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <AssignTaskModal
        task={assigningTask}
        profiles={profileOptions}
        selectedAssignee={selectedAssignee}
        onSelectedAssigneeChange={setSelectedAssignee}
        onClose={closeAssignModal}
        onAssign={handleAssignTask}
        isLoading={updateTaskMutation.isPending}
        error={assignError}
      />

    </div>
  )
}
